import { trackExternalLinkOpened } from "@/utils/analytics";
import { openUrlSafely } from "@/utils/linking";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { Stack, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const ECO_DEFIS_URL = "https://www.ge.ch/dossier/durabilite-climat/climat/eco-defis";

export default function AboutScreen() {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <SafeAreaView style={styles.container} edges={["top"]}>
      <StatusBar style="dark" />
      <Stack.Screen options={{ headerShown: false }} />

      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 0.35]}
        style={styles.gradient}
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
            <Ionicons name="chevron-back" size={24} color="#1C453F" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t("about.headerTitle")}</Text>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.intro}>
            {t("about.introPrefix")}{" "}
            <Text
              style={styles.link}
              onPress={() => {
                trackExternalLinkOpened(ECO_DEFIS_URL);
                openUrlSafely(ECO_DEFIS_URL, {
                  title: t("common.linkErrorTitle"),
                  message: t("common.linkErrorMessage"),
                });
              }}
            >
              {t("about.introLink")}
            </Text>{" "}
            {t("about.introSuffix")}
          </Text>

          <Text style={styles.text}>
            {t("about.paragraphAudience")}
          </Text>

          <Text style={styles.text}>
            {t("about.paragraphAxes")}
          </Text>
          <BulletPoint text={t("about.bulletLocal")} />
          <BulletPoint text={t("about.bulletBalanced")} />
          <BulletPoint text={t("about.bulletZeroWaste")} />

          <View style={{ height: 16 }} />

          <Section title={t("about.howTitle")}>
            <Text style={styles.text}>
              {t("about.howText")}
            </Text>
          </Section>

          <Section title={t("about.contactTitle")}>
            <Text style={styles.text}>
              {t("about.contactText")}{"\n"}
              <Text style={styles.textBold}>eco-defis@etat.ge.ch</Text>
            </Text>
          </Section>

          <View style={{ height: 32 }} />
        </ScrollView>
      </LinearGradient>
    </SafeAreaView>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function BulletPoint({ text }: { text: string }) {
  return (
    <View style={styles.bulletRow}>
      <Text style={styles.bullet}>•</Text>
      <Text style={styles.bulletText}>{text}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FBEFE3",
  },
  gradient: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#FFF",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#1C453F",
  },
  content: {
    paddingHorizontal: 24,
    paddingTop: 8,
  },
  intro: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 15,
    color: "#1C453F",
    lineHeight: 22,
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#1C453F",
    marginBottom: 8,
  },
  text: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#333",
    lineHeight: 22,
    marginBottom: 8,
  },
  textBold: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#1C453F",
  },
  link: {
    fontFamily: "Inter_600SemiBold",
    color: "#CD7728",
    textDecorationLine: "underline",
  },
  bulletRow: {
    flexDirection: "row",
    paddingLeft: 8,
    marginBottom: 4,
  },
  bullet: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#CD7728",
    marginRight: 8,
  },
  bulletText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#333",
    lineHeight: 22,
    flex: 1,
  },
});
